import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { HTTP } from "../../api/resources";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import NoListEditor from "../Helpers/Utilities/NoListEditor";
import SponsorshipTable from "../Sponsorship/SponsorshipTable";
import Select from "react-select";
import UserGroupForm from "../Groups/UserGroupForm";
import PageBlocking from "./Memberships/PageBlocking";
import PageMemberships from "./Memberships/PageMemberships";
import { DirectUpload } from "@rails/activestorage";
import ConfirmationModal from "../Helpers/Modal/ConfirmationModal";
import { SketchPicker } from "react-color";
import PageSponsorshipAmount from "../Helpers/Modal/Forms/PageSponsorshipAmount";
import CustomField from "../Helpers/Modal/Forms/CustomField"
import PageSummary from "../Helpers/Modal/Forms/PageSummary"
import { padEnd, set } from "lodash";
import CustomizeSponsorshipModal from "./Modals/CustomizeSponsorshipModal";

export default function PagesEdit(props) {
  const [page, setPage] = useState(props.page);
  const [saving, setSaving] = useState(false);
  const team = props.team;
  const titles = props.titles;
  const membersInfo = props.members_info;
  const blockedMembers = props.blocked_members;
  const [fileUploading, setFileUploading] = useState(false);
  const [openFeeModal, setOpenFeeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [users, setUsers] = useState(props.users);
  const [amounts, setAmounts] = useState(props.amounts);
  const [activeTab, setActiveTab] = useState("primary");
  const [myPrimaryColor, setMyPrimaryColor] = useState(page.primary);
  const [mySecondaryColor, setMySecondaryColor] = useState(page.secondary);
  const [myButtonColor, setMyButtonColor] = useState(page.button_color)
  const [myTextColor, setMyTextColor] = useState(page.text_color)
  const [isDirty, setIsDirty] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selectedSponsorshipAmount, setSelectedSponsorshipAmount] = useState(null);
  const [userAmounts, setUserAmounts] = useState([]);
  const [pages, setPages] = useState(props.pages);
  const [customFields, setCustomFields] = useState(props.customFields)
  const [openFields, setOpenFields] = useState(false)
  const [inputFields, setInputFields] = useState(props.inputFields)
  const [inputField, setInputField] = useState({label: '', id: null})
  const [editInputField, setEditInputField] = useState(null)
  const [processingFields, setProcessingFields] = useState(false)
  const [customSummaries, setCustomSummaries] = useState(props.customSummaries)
  const [showAllSummaries, setShowAllSummaries] = useState(false)
  const [descriptionModal, setDescriptionModal] = useState(null)
  const [initializedType, setInitializedType] = useState(props.page.page_type)
  const [showSponsorshipModal, setShowSponsorshipModal] = useState(false)
  const [templates, setTemplates] = useState(props.templates)
  let image = null;
  let allowedSponsorshipModal = page.project_sponsorship_type == 'fixed' || page.project_sponsorship_type == 'multiple'

  // variables and functions initializations
  useEffect(() => {
    if (!page.have_set_colors && team.secondary_theme) {
      setMyPrimaryColor(team.secondary_theme);
      setMyButtonColor(team.secondary_theme)
      setPage({
        ...page,
        primary: team.secondary_theme,
        button_color: team.secondary_theme,
        have_set_colors: true,
      });
    }

    if (page.have_set_colors && !page.button_color && page.primary) {
      setMyButtonColor(page.primary)
      setPage({...page, button_color: page.primary})
    }
  }, []);

  useEffect(() => {
    if (page.use_payments && !page.use_stripe && !page.use_team_payments) {
      if (team.stripe_uid) setPage({ ...page, use_stripe: true });
      if (!team.stripe_uid) setPage({ ...page, use_team_payments: true });
    }
  }, [page]);

  useEffect(() => {
    if (page.page_type == 'project') {
      setTemplates(props.templates.filter(template => template.type == 'project'))
    }

    if (page.page_type == 'user') {
      setTemplates(props.templates.filter(template => template.type == 'user'))
    }

    if (page.is_iframe_page) {
      setTemplates(props.templates.filter(template => template.type == 'iframe_user'))
      if (page.template !== 'circle_profiles' && page.template !== 'square_profiles') {
        setPage({...page, template: 'circle_profiles'})
      }
    }

    if (page.page_type == 'user' && (page.template == 'plain_sponsorship' || page.template == 'sponsorship_posts')) {
      setPage({...page, template: 'static'})
    }

    if (page.page_type == 'project' && page.template !== 'plain_sponsorship' && page.template !== 'sponsorship_posts') {
      setPage({...page, template: 'plain_sponsorship'})
    }
  }, [page.page_type])

  // end of initializations 

  // handle change of colors in the sketch picker
  const handleColorChange = (color) => {
    const hexCode = `${color.hex}${decimalToHex(
      color?.rgb?.a ?? defaultAlpha
    )}`;

    if (activeTab === "primary") setMyPrimaryColor(hexCode);
    if (activeTab == 'secondary') setMySecondaryColor(hexCode);
    if (activeTab == 'button_color') setMyButtonColor(hexCode);
    if (activeTab == 'text_color') setMyTextColor(hexCode);

    setPage((page) => {
      if (activeTab === "primary") return { ...page, primary: hexCode, have_set_colors: true };
      if (activeTab === "secondary") return { ...page, secondary: hexCode, have_set_colors: true };
      if (activeTab === "button_color") return { ...page, button_color: hexCode, have_set_colors: true };
      if (activeTab === "text_color") return { ...page, text_color: hexCode, have_set_colors: true };
    });
    setIsDirty(true);
  };

  const decimalToHex = (alpha) => {
    let aHex = Math.round(255 * alpha).toString(16);
    return alpha === 0 ? "00" : aHex.length < 2 ? `0${aHex}` : aHex;
  };

  // submit page data to backend
  const handleSubmit = () => {
    // validate page data
    if (!validateData()) return;

    // update page request
    setSaving(true);
    HTTP.patch(`/pages/${props.page.id}`, {
      page: page,
    })
      .then((response) => {
        Alert.show("notice", "Page saved!");
        setPage(response.data);
        setSaving(false);
        setIsDirty(false);
        setInitializedType(response.data.page_type)
      })
      .catch((e) => {
        Alert.show("error", "Could not save. Try again or refresh.");
        setSaving(false);
      });
  };

  // validate inputed data
  const validateData = () => {
    if (!page.title) {
      Alert.show("notice", "Name or Title is a required field");
      return false;
    }

    if (!page.public && !page.is_iframe_page) return true;

    if (!page.public && page.is_iframe_page) {
      if (!page.iframe_height || page.iframe_height < 100) {
        Alert.show('notice', 'The minimum height is 100px')
        return false
      }
    }

    // validate that a payment method is selected
    if (page.use_payments && !page.use_stripe && !page.use_team_payments) {
      Alert.show("noice", "Payment method not selected");
      return false;
    }

    if (
      page.use_payments &&
      page.use_team_payments &&
      !page.team_payment_link
    ) {
      Alert.show("noice", "Payment portal link not provided");
      return false;
    }

    if (
      page.use_payments &&
      page.use_stripe &&
      page.page_type == 'user' &&
      !page.general_sponsorship_amount
    ) {
      Alert.show("notice", "General sponsorship amount is required");
      return false;
    }

    if (page.template !== "static" && page.redirect_link) {
      if (
        page.redirect_link.includes("://") ||
        page.redirect_link.includes(" ")
      ) {
        Alert.show(
          "notice",
          "Redirect link cannot include protocol e.g https:// or spaces"
        );
        return false;
      }
    }

    if (page.logo_link) {
      if (
        page.logo_link.includes("://") ||
        page.logo_link.includes(" ")
      ) {
        Alert.show(
          "notice",
          "Logo link cannot include protocol e.g https:// or spaces"
        );
        return false;
      }
    }

    if (page.use_payments && page.use_team_payments && page.team_payment_link) {
      if (
        page.team_payment_link.includes("://") ||
        page.team_payment_link.includes(" ")
      ) {
        Alert.show(
          "notice",
          "Payment link cannot include protocol e.g https:// or spaces"
        );
        return false;
      }
    }

    let regularExpression = /^[a-zA-Z\s-]+$/;

    if (page.custom_url_endpoint && !regularExpression.test(page.custom_url_endpoint) && page.public) {
        Alert.show(
            'notice', 
            'Custom Slug can only include normal text and "-", other characters are not allowed'
        );
        return false;
    }

    let usedEndpoints = props.usedEndpoints ? props.usedEndpoints : []

    if (page.custom_url_endpoint && usedEndpoints.includes(page.custom_url_endpoint) && page.public) {
      Alert.show(
        'notice', 
        'Custom Slug already in use by one of your pages, try another slug or remove from the other page'
      )
      return false
    }

    if (page.page_type == 'project' && page.project_sponsorship_type == 'fixed' && !page.project_fixed_amount && page.public) {
      Alert.show('notice', 'You must enter sponsorship amount for fixed sponsorship type')
      return false 
    }

    if (page.page_type == 'project' && page.project_sponsorship_type == 'multiple' && (!page.project_sponsorship_plans || page.project_sponsorship_plans < 1) && page.public) {
      Alert.show('notice', 'At least one sponsorship amount is needed')
      return false
    }

    if (
        page.page_type == 'project' && page.use_goal_tracking && page.use_payments 
        && page.use_stripe && (!page.target_amount || page.target_amount < 0) && page.public
      ) 
      {
        Alert.show('notice', 'Target amount is required and must be greater than $0')
        return false
      }


    return true;
  };

  // add new field to the list of fields
  const handleFieldUpdate = (event) => {
    const value = event.target.value;
    let newFields = page.settings["fields"] || [];
    if (event.target.checked) {
      newFields.push({ id: value });
    } else {
      _.remove(newFields, (n) => n.id == value);
    }

    setPage((page) => {
      return { ...page, settings: { ...page.settings, fields: newFields } };
    });
    setIsDirty(true);
  };

  const pageFields = (page.settings["fields"] || []).map((field) => field.id);

  const hiddenFileInput = React.useRef(null);

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const handleChange = (event) => {
    Alert.show("notice", "Uploading image, please wait....");
    const files = event.target.files;
    // upload only the first file
    uploadFile(files[0]);
  };

  const uploadFile = (file) => {
    // Check for .jiff extension
    if (file.name.endsWith('.jfif')) {
      Alert.show('error', '.jiff image is not allowed');
      return;
    }

    setFileUploading(true);
    const upload = new DirectUpload(file, "/api/v1/direct_uploads");
    upload.create((error, blob) => {
      if (error) {
        // Handle the error
        Alert.show("error", "Could not update. Try again.");
        setFileUploading(false);
      } else {
        setTimeout(() => {
          setFileUploading(false);
        }, 5000);
        image = blob;
        setPage({ ...page, banner_image: image.cdn_url });
        Alert.show("notice", "Image has been added");
      }
    });
  };

  useEffect(() => {}, [page.banner_image]);

  const handlePaymentType = (checked) => {
    if (checked) {
      if (team.stripe_uid)
        setPage({ ...page, use_team_payments: false, use_stripe: true });
      if (!team.stripe_uid)
        Alert.show(
          "notice",
          "You have to connect to Stripe in the settings to use Stripe payments"
        );
    }

    if (!checked) {
      setPage({ ...page, use_team_payments: true, use_stripe: false });
    }
  };

  const handleProcessingFee = (checked) => {
    if (checked) {
      setOpenFeeModal(true);
      return;
    }

    setPage({ ...page, pass_processing_fee: false });
  };

  const getLabel = (user) => {
    if (!amounts) {
      return `${user.first_name} ${user.last_name} - $${page.general_sponsorship_amount}`;
    }

    let currectAmount = amounts.find((amount) => amount.user_id == user.id);

    if (currectAmount) {
      return `${user.first_name} ${user.last_name} - $${currectAmount.amount}`;
    } else {
      return `${user.first_name} ${user.last_name} - $${page.general_sponsorship_amount}`;
    }
  };

  window.onbeforeunload = (event) => {
    if (isDirty) {
      return "";
    }
  };

  const handleViewPage = () => {
    if (isDirty) {
      Alert.show(
        "notice",
        "You have unsaved changes, please save before proceeding"
      );
      return;
    }

    window.open(`${page.url}`, "_blank");
  };

  const handleFetchGroupUsers = async (groupId) => {
    // if no group was selected
    if (!groupId) {
      setUsers([]);
      setAmounts([]);
      return;
    }

    // send a get request to the backend
    try {
      const response = await fetchGroupUsers(groupId);
      setAmounts(response.data.amounts ? response.data.amounts : []);
      setUsers(response.data.users ? response.data.users : []);
    } catch {
      Alert.show("error", "Could not fetch group users, please try again");
      return;
    }
  };

  const fetchGroupUsers = (groupId) => {
    return HTTP.get(`/lazyload/group/users/${groupId}/${page.id}`);
  };

  // decide how many users will be shown and return them
  const decideUsersCount = () => {
    let holder = null;

    if (showAll) return users;
    if (!showAll) return users.slice(0, 5);
  };
  
  const showUsersSummaries = () => {
    if (showAllSummaries) return users 
    if (!showAllSummaries) return users.slice(0, 5)
  }

  // return the amount of sponsorships
  const returnSponsorshipsCount = (user) => {
    let userAmounts = amounts.filter((amount) => amount.user_id == user.id);
    if (userAmounts && userAmounts.length > 0) return userAmounts.length;
    return 1;
  };

  // return the total amount of a user sponsorship
  const returnTotalSponsorshipAmount = (user) => {
    let userAmounts = amounts.filter((amount) => amount.user_id == user.id);
    let totalAmount = page.general_sponsorship_amount;
    if (userAmounts && userAmounts.length > 0) {
      totalAmount = 0;
      userAmounts.map((amount) => (totalAmount = totalAmount + amount.amount));
      return totalAmount;
    } else {
      return totalAmount;
    }
  };

  // open the sponsorship amount modal and set some values
  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setUserAmounts(amounts.filter((amount) => amount.user_id == user.id));
    setSelectedSponsorshipAmount(true);
  };

  // close the sponsorship amount modal and clear values
  const handleCloseModal = (arg) => {
    setAmounts(arg);
    setSelectedUser(null);
    setUserAmounts([]);
    setSelectedSponsorshipAmount(false);
  };

  const getPageTitle = (id) => {
    let data = pages.find((p) => p.id == id)
    return data ? data.title : ''
  };

  const getFieldOptions = () => {
    let ids = []

    if (inputFields && inputFields.length > 0) {
      inputFields.map(field => ids.push(field.team_custom_field_id))
    }
    
    if (customFields && customFields.length > 0) {
      let filteredFields = customFields.filter(field => {
        return field.id !== null && !ids.includes(field.id)
      })
      return filteredFields ? [{id: null, name: 'No selection'},{id: "popup", name: 'Create New Field'}, ...filteredFields] : []
    }

    return []
  }

  const getCustomField = (id) => {
    if (id) {
      let field = customFields.find(f => f.id == id)
      return field ? field : null
    }
    return null
  }

  const createField = async () => {
    setProcessingFields(true)

    if (!editInputField) {
      try {
        let response = await createFieldRequest()
        setInputFields(response.data)
        setInputField({label: '', id: null})
        Alert.show('notice', 'New input field created successfully')
        setProcessingFields(false)
      } catch {
        Alert.show('error', 'Could not create new input field')
        setProcessingFields(false)
      }
    } else {
      try {
        let response = await updateFieldRequest()
        setInputFields(response.data)
        setInputField({label: '', id: null})
        setEditInputField(null)
        Alert.show('notice', 'Input field updated successfully')
        setProcessingFields(false)
      } catch {
        Alert.show('error', 'Could not update input field')
        setProcessingFields(false)
      }
    }
  }

  const deleteField = async (id) => {
    setProcessingFields(true)
    try {
      let response = await deleteFieldRequest(id)
      setInputFields(response.data)
      Alert.show('notice', 'Field deleted successfully')
      setProcessingFields(false)
    } catch {
      Alert.show('error', 'Deleted field successfully')
      setProcessingFields(false)
    }
  }

  const createFieldRequest = () => {
    return HTTP.post(`/page/input_fields/${page.id}`, {label: inputField.label, team_custom_field_id: inputField.id})
  }

  const updateFieldRequest = () => {
    return HTTP.patch(`/page/input_fields/${editInputField}`, {label: inputField.label, team_custom_field_id: inputField.id})
  }

  const deleteFieldRequest = (id) => {
    return HTTP.delete(`/page/input_fields/${id}`)
  }

  const getCustomDescription = (user) => {
    if (!customSummaries) return 'Not provided, default description will be used'
    let record = customSummaries.find(summary => summary.user_id == user.id)
    return record ? 'Provided and will be used on the user page' : 'Not provided, default description will be used'
  }

  const getCustomDescriptionObject = (user) => {
    if (!customSummaries) return false
    let record = customSummaries.find(summary => summary.user_id == user.id)
    return record ? record : false
  }

  // color displayed in sketch picker 
  const displayedColor = () => {
    if (activeTab === 'primary') return myPrimaryColor
    if (activeTab === 'secondary') return mySecondaryColor
    if (activeTab === 'button_color') return myButtonColor
    if (activeTab === 'text_color') return myTextColor
  }

  const copyIframeTag = () => {
    navigator.clipboard.writeText(currentTag())
    Alert.show('notice', 'Copied Iframe tag to clipboard')
  }

  const currentTag = () => {
    let tag = `<Iframe src="${page.iframe_src}" style="width: 100%; height: ${page.iframe_height}px; border: none;" />`
    return tag
  }

  return (
    <>
      <div className="page-header d-flex align-items-baseline">
        <h4 className="">
          <a href="/pages">
            <i className="fa fa-chevron-left mr-2"></i> Back
          </a>
        </h4>
        <ul className="breadcrumbs">
          <li className="nav-item">Pages</li>

          <li className="separator">
            <i className="fa fa-arrow-right"></i>
          </li>
          <li className="nav-item">{page.title}</li>
        </ul>
      </div>

      {!page.public && page.is_iframe_page && 
        <div className="card">
          <div className="card-header flex-header">
            <div>
              <h4 className="card-title">Iframe Tag</h4>
            </div>
            <div>
              <button onClick={copyIframeTag} className="btn btn-primary">
                Copy Tag
              </button>
            </div>
          </div>

          <div className="card-body">
            <div className="form-group">
              <h5>
                <pre>
                  <code>
                    {currentTag()}
                  </code>
                </pre>
              </h5>
            </div>
          </div>
        </div>
      }

      {!page.public && !page.is_iframe_page && page.banner_image && (
        <div class="card" style={{ height: "200px" }}>
          <div class="row w-100 h-100 margin-auto" style={{ margin: "auto" }}>
            <img
              src={page.banner_image}
              class="img-fluid w-100 h-100"
              alt="Responsive image"
            />
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-header flex-header">
          <h4 className="card-title">Page Editor</h4>

          <div>
            {page.public && page.title && page.title.length > 0 && (
              <a
                onClick={handleViewPage}
                target="blank"
                className="btn btn-secondary mr-2"
                aria-disabled
              >
                <i className="fa fa-external-link mr-2"></i>
                View
              </a>
            )}
            <button
              onClick={handleSubmit}
              name="button"
              className="btn btn-primary"
            >
              {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
              Save
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-9">
              <div className="form-group">
                <label htmlFor="title">
                  {page.public && <>Title</>}
                  {!page.public && <>Name</>}
                </label>
                <input
                  className="form-control"
                  placeholder="New Page"
                  type="text"
                  name="title"
                  id="title"
                  value={page.title}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPage((page) => {
                      return { ...page, title: val };
                    });
                    setIsDirty(true);
                  }}
                />
                {page.public && (
                  <div className="mt-1">
                    <a href={`${page.url}`} target="blank">
                      {page.url}
                    </a>
                  </div>
                )}
              </div>
              {!page.is_iframe_page &&
                <div className="form-group">
                  <label htmlFor="body">
                    {page.public && <>Content</>}
                    {!page.public && <>Description</>}
                  </label>
                  <NoListEditor
                    value={page.body}
                    returnChange={(value) => setPage({ ...page, body: value })}
                  />
                  <div className="mt-1">
                    For best results, please use child profile pictures that are
                    300px by 300px or greater in size.
                  </div>
                </div>
              }

              {page.use_payments && page.use_stripe && page.use_goal_tracking && page.page_type == 'project' && (
                <div className="form-group">
                  <label htmlFor="target_amount">Donation Goal (USD)</label>
                  <input
                    className="form-control"
                    placeholder="Enter target amount"
                    type="number"
                    name="target_amount"
                    id="target_amount"
                    value={page.target_amount}
                    onChange={(e) => {
                      setPage({
                        ...page,
                        target_amount: e.target.value,
                      });
                      setIsDirty(true);
                    }}
                  />
                </div>
              )}

              {page.use_payments && page.use_stripe && page.use_goal_tracking && page.page_type == 'project' && (
                <div className="form-group">
                  <label htmlFor="amount_raised">Amount Raised { page.target_type == 'monthly' ? 'Monthly (USD)' : '(USD)' }</label>
                  <input
                    className="form-control"
                    placeholder="Amount raised"
                    type="number"
                    name="amount_raised"
                    id="amount_raised"
                    value={parseFloat(page.amount_raised).toFixed(2)}
                    disabled
                  />
                </div>
              )}

              {(page.public || page.is_iframe_page) &&
                <div className="form-group">
                    <label htmlFor="logo_link">Logo Link</label>
                    <input
                      className="form-control"
                      placeholder="example.com"
                      type="text"
                      name="logo_link"
                      id="logo_link"
                      value={page.logo_link}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage((page) => {
                          return { ...page, logo_link: val };
                        });
                        setIsDirty(true);
                      }}
                    />
                  </div>
              }

              {(page.public || page.is_iframe_page) && page.template !== "static" && (
                <>
                  {page.template !== "static" && page.page_type == 'user' && page.public && (
                    <div className="form-group">
                      <label htmlFor="page_link">Link Page Actions</label>
                      <div className="input-group">
                        <Select
                          className="w-100 select-audience"
                          classNamePrefix="select-audience"
                          isMulti
                          value={page.linked_pages}
                          onChange={(value) =>
                            setPage({ ...page, linked_pages: value })
                          }
                          options={props.pageIds}
                          getOptionLabel={(option) => `${getPageTitle(option)}`}
                          getOptionValue={(option) => option}
                        />
                      </div>
                      <div className="mt-1">
                        {titles.beneficiary} fully sponsored on one page are
                        automatically removed from the other, and vice versa.
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="link">Completion Redirect Link</label>
                    <input
                      className="form-control"
                      placeholder="example.com"
                      type="text"
                      name="link"
                      id="link"
                      value={page.redirect_link}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage((page) => {
                          return { ...page, redirect_link: val };
                        });
                        setIsDirty(true);
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="text">Button Text</label>
                    <input
                      className="form-control"
                      placeholder="Sponsor"
                      type="text"
                      name="text"
                      id="text"
                      value={page.button_text}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage((page) => {
                          return { ...page, button_text: val };
                        });
                        setIsDirty(true);
                      }}
                    />
                  </div>

                  {page.template !== "static" &&
                    page.use_payments &&
                    page.use_team_payments && (
                      <div className="form-group">
                        <label htmlFor="text">Payment Portal Link</label>
                        <input
                          className="form-control"
                          placeholder="example.com"
                          type="text"
                          name="text"
                          id="text"
                          value={page.team_payment_link}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPage((page) => {
                              return { ...page, team_payment_link: val };
                            });
                            setIsDirty(true);
                          }}
                        />
                      </div>
                    )}

                  {!page.use_payments && (
                    <div className="form-group">
                      <label htmlFor="text">Sign Up Form Text</label>
                      <input
                        className="form-control"
                        placeholder="Next you will be redirected to ....."
                        type="text"
                        name="text"
                        id="text"
                        value={page.inbound_text}
                        onChange={(e) => {
                          setPage({ ...page, inbound_text: e.target.value });
                          setIsDirty(true);
                        }}
                      />
                      <div className="mt-1">
                        Will be shown under the sign up button, a default will
                        be rendered if this is left blank
                      </div>
                    </div>
                  )}

                  {page.use_payments && page.use_team_payments && (
                    <div className="form-group">
                      <label htmlFor="text">Sign Up Form Text</label>
                      <input
                        className="form-control"
                        placeholder="Next you will be redirected to ....."
                        type="text"
                        name="text"
                        id="text"
                        value={page.team_payment_text}
                        onChange={(e) => {
                          setPage({
                            ...page,
                            team_payment_text: e.target.value,
                          });
                          setIsDirty(true);
                        }}
                      />
                      <div className="mt-1">
                        Will be shown under the sign up button, a default will
                        be rendered if this is left blank
                      </div>
                    </div>
                  )}

                  {page.use_payments && page.use_stripe && (
                    <div className="form-group">
                      <label htmlFor="text">Sign Up Form Text</label>
                      <input
                        className="form-control"
                        placeholder="Next you will be redirected to ....."
                        type="text"
                        name="text"
                        id="text"
                        value={page.stripe_payment_text}
                        onChange={(e) => {
                          setPage({
                            ...page,
                            stripe_payment_text: e.target.value,
                          });
                          setIsDirty(true);
                        }}
                      />
                      <div className="mt-1">
                        Will be shown under the sign up button, a default will
                        be rendered if this is left blank
                      </div>
                    </div>
                  )}

                  {props.customDomain && props.domainFlag && page.public && (
                    <div className="form-group">
                      <label htmlFor="text">Custom URL Slug</label>
                      <input
                        className="form-control"
                        placeholder="Enter custom slug e.g 'sponsorship', 'donation'"
                        type="text"
                        name="text"
                        id="text"
                        value={page.custom_url_endpoint}
                        onChange={(e) => {
                          setPage({
                            ...page,
                            custom_url_endpoint: e.target.value.toLowerCase(),
                          });
                          setIsDirty(true);
                        }}
                      />
                      <div className="mt-1">
                        {!page.custom_url_endpoint && <>Enter slug to preview URL</>}
                        {page.custom_url_endpoint && <>Preview: https://{props.customDomain}/{page.custom_url_endpoint}</>}
                      </div>
                    </div>
                  )}
                </>
              )}
              {(page.public || page.is_iframe_page) && (
                <div className="form-group">
                  <div>
                    <label className="form-label">Public fields</label>
                  </div>
                  <div className="selectgroup selectgroup-pills">
                    {page.available_fields &&
                      page.available_fields.map((field) => (
                        <label key={field.id} className="selectgroup-item">
                          <input
                            type="checkbox"
                            name="value"
                            value={field.id}
                            className="selectgroup-input"
                            onChange={handleFieldUpdate}
                            checked={pageFields.includes(field.id.toString())}
                            disabled={initializedType !== page.page_type}
                          />
                          <span className="selectgroup-button">
                            {field.name}
                          </span>
                        </label>
                      ))}
                  </div>
                  {initializedType !== page.page_type && 
                    <small className="text-danger">Save to load correct custom fields for this page type</small>
                  }
                </div>
              )}

              {(page.public || page.is_iframe_page) && (
                <div className="form-group">
                  <label htmlFor="organization_name">Choose Page Themes</label>
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${
                          activeTab === "primary" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("primary")}
                      >
                        Primary
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${
                          activeTab === "secondary" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("secondary")}
                      >
                        Secondary
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${
                          activeTab === "button_color" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("button_color")}
                      >
                        Button Color
                      </a>
                    </li>
                    {(page.template == 'sponsorship_video_2' || page.page_type == 'project') && 
                      <li className="nav-item">
                        <a
                          className={`nav-link ${
                            activeTab === "text_color" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("text_color")}
                        >
                          Text Color
                        </a>
                      </li>
                    }
                    {!page.public && page.is_iframe_page && 
                      <li className="nav-item">
                        <a
                          className={`nav-link ${
                            activeTab === "text_color" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("text_color")}
                        >
                          Text Color
                        </a>
                      </li>
                    }
                  </ul>
                  <SketchPicker
                    className="mt-3"
                    color={displayedColor()}
                    onChange={(e) => handleColorChange(e)}
                  />
                </div>
              )}
            </div>
            <div className="col-md-3">
              {page.public && (
                <div className="form-group">
                  <label>Status</label>
                  <div>
                    <ToggleSwitch
                      small
                      id="status"
                      checked={page.status == "published"}
                      onChange={(checked) => {
                        const status = checked ? "published" : "draft";
                        setPage((page) => {
                          return { ...page, status: status };
                        });
                        setIsDirty(true);
                      }}
                    />
                    <label
                      style={{ textTransform: "capitalize" }}
                      htmlFor="status"
                    >
                      {page.status}
                    </label>
                  </div>
                </div>
              )}

              {!page.public && page.is_iframe_page && 
                <div className="form-group">
                  <label>Transparent Background</label>
                  <div>
                    <ToggleSwitch
                      small
                      id="background_style"
                      checked={page.transparent_background}
                      onChange={(checked) => {
                        setPage((page) => {
                          return { ...page, transparent_background: checked };
                        });
                        setIsDirty(true);
                      }}
                    />
                    <label htmlFor="background_style">
                      {page.transparent_background ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                </div>
              }

              {!page.public && !page.is_iframe_page && (
                <div className="form-group">
                  <label>Status</label>
                  <div>
                    <ToggleSwitch
                      small
                      id="community_status"
                      checked={page.archived}
                      onChange={(checked) => {
                        setPage({ ...page, archived: checked });
                        setIsDirty(true);
                      }}
                    />
                    <label htmlFor="community_status">
                      Archive
                    </label>
                  </div>
                </div>
              )}

              {page.public && page.template !== "static" && (
                <div className="form-group">
                  <label>Visibility</label>
                  <div>
                    <ToggleSwitch
                      small
                      id="show_to_donor"
                      checked={page.show_to_donor}
                      onChange={(checked) => {
                        setPage((page) => {
                          return { ...page, show_to_donor: checked };
                        });
                        setIsDirty(true);
                      }}
                    />
                    <label htmlFor="show_to_donor">
                      {page.show_to_donor
                        ? `Show To ${titles.donor}`
                        : `Hide From ${titles.donor}`}
                    </label>
                  </div>
                </div>
              )}

              {(page.public || page.is_iframe_page) && page.template !== "static" && (
                <div className="form-group">
                  <label>Payments</label>
                  <div>
                    <ToggleSwitch
                      small
                      id="use_payments"
                      checked={page.use_payments}
                      onChange={(checked) => {
                        setPage({ ...page, use_payments: checked });
                        setIsDirty(true);
                      }}
                    />
                    <label htmlFor="use_payments">
                      {page.use_payments
                        ? "Using Payments"
                        : "Payments Disabled"}
                    </label>
                  </div>
                </div>
              )}

              {(page.public || page.is_iframe_page) &&
                page.template !== "static" &&
                page.use_payments && (
                  <div className="form-group">
                    <label>Payment Type</label>
                    <div>
                      <ToggleSwitch
                        small
                        id="payment_type"
                        checked={page.use_stripe}
                        onChange={(checked) => {
                          handlePaymentType(checked);
                          setIsDirty(true);
                        }}
                      />
                      <label htmlFor="payment_type">
                        {page.use_stripe
                          ? "Using Stripe Payments"
                          : "Using Team Payments"}
                      </label>
                    </div>
                  </div>
                )}

                {(page.public || page.is_iframe_page) && page.template !== "static" && page.use_payments && page.use_stripe && page.page_type == 'project' && (
                  <div className="form-group">
                    <label>Pass Processing Fee</label>
                    <div>
                      <ToggleSwitch
                        small
                        id="pass_processing_fee"
                        checked={page.pass_processing_fee}
                        onChange={(checked) => {
                          setPage({...page, pass_processing_fee: checked});
                          setIsDirty(true);
                        }}
                      />
                      <label htmlFor="pass_processing_fee">
                        {page.pass_processing_fee
                          ? "Enabled"
                          : "Disabled"}
                      </label>
                    </div>
                  </div>
                )}

              {(page.public || page.is_iframe_page) &&
                page.template !== "static" && (
                  <div className="form-group">
                    <label>Capture Signup Information</label>
                    <div>
                      <ToggleSwitch
                        small
                        id="collect_sponsor_address"
                        checked={page.collect_sponsor_address}
                        onChange={(checked) => {
                          setPage({...page, collect_sponsor_address: checked});
                          setIsDirty(true);
                        }}
                      />
                      <label htmlFor="collect_sponsor_address">
                        {page.collect_sponsor_address
                          ? "Enabled"
                          : "Disabled"}
                      </label>
                    </div>
                  </div>
                )}

              {page.public && page.page_type == 'project' && page.use_stripe && page.use_payments && (
                <div className="form-group">
                  <label>Set A Donation Goal</label>
                  <div>
                    <ToggleSwitch
                      small
                      id="use_goal_tracking"
                      checked={page.use_goal_tracking}
                      onChange={(checked) => {
                        setPage({...page, use_goal_tracking: checked});
                        setIsDirty(true);
                      }}
                    />
                    <label htmlFor="use_goal_tracking">
                      {page.use_goal_tracking
                        ? "Enabled"
                        : "Disabled"}
                    </label>
                  </div>
                </div>
              )}

              {!page.public && !page.is_iframe_page && (
                <div className="form-group">
                  <label htmlFor="profile_image">{`Cover Image`}</label>
                  <div id="profile_image" className="d-flex align-items-center">
                    <button onClick={handleClick} className="btn btn-primary">
                      {fileUploading && (
                        <i className="fa fa-spinner-third fa-spin mr-2"></i>
                      )}
                      Upload Image
                    </button>
                  </div>
                  <div>
                    <small className="text-muted">
                      Optimal Banner Photo Size: 1200px X 421px
                    </small>
                  </div>
                  <input
                    type="file"
                    ref={hiddenFileInput}
                    style={{ display: "none" }}
                    onChange={handleChange}
                  />
                </div>
              )}

              {page.public && page.use_payments && page.page_type == 'project' && (
                <>
                  <div className="form-group">
                    <label htmlFor="target_type">Goal Type</label>
                    <select
                      value={page.target_type}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage({ ...page, target_type: val });
                        setIsDirty(true);
                      }}
                      className="form-control capybara-page-type"
                      id='target_type'
                      disabled={page.amount_raised}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="one-time">One Time</option>
                    </select>
                  </div>
                </>
              )}

              {page.public && (
                <>
                  <div className="form-group">
                    <label htmlFor="page_type">Page Type</label>
                    <select
                      value={page.page_type}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage({ ...page, page_type: val, settings: { fields: [] }});
                        setIsDirty(true);
                      }}
                      className="form-control capybara-page-type"
                      id='page_type_selection'
                    >
                      <option value="user">{ props.beneficiary_title }</option>
                      <option value="project" className='capybara-project'>{ props.project_title }</option>
                    </select>
                  </div>
                </>
              )}

              {page.public && page.page_type && page.page_type == 'project' && (
                <>
                  <div className="form-group">
                    <label htmlFor="project">{ props.project_title }</label>
                    <select 
                      value={page.project_id}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage({ ...page, project_id: val });
                        setIsDirty(true);
                      }}
                      className="form-control"
                    >
                      <option value=''>No { props.project_title }</option>
                      {props.projects.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {(page.public || page.is_iframe_page) && (
                <>
                  <div className="form-group">
                    <label htmlFor="template">Template</label>
                    <select
                      value={page.template}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage((page) => {
                          return { ...page, template: val };
                        });
                        setIsDirty(true);
                      }}
                      className="form-control"
                    >
                      {templates.map((option) => (
                        <option key={option.name} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {page.page_type !== 'project' &&
                    <div className="form-group">
                      <label htmlFor="group">Group</label>
                      <select
                        value={page.group_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPage((page) => {
                            return { ...page, group_id: val };
                          });
                          handleFetchGroupUsers(val);
                        }}
                        className="form-control"
                      >
                        <option value="">No Group</option>
                        {props.groups.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  }

                  <div className="form-group">
                    <label htmlFor="font">Page Font</label>
                    <select
                      value={page.font_name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPage((page) => {
                          return { ...page, font_name: val };
                        });
                      }}
                      className="form-control"
                    >
                      <option value="Lato, sans-serif">Lato, sans-serif (default)</option>
                      <option value="Roboto, sans-serif">Roboto, sans-serif</option>
                      <option value="Avenir, sans-serif">Avenir, sans-serif</option>
                      <option value="Avenir Next, sans-serif">Avenir Next, sans-serif</option>
                      <option value="Helvetica Nue, sans-serif">Helvetica Nue, sans-serif</option>
                      <option value="Courier, monospace">Courier, monospace</option>
                      <option value="Pacifico, cursive">Pacifico, cursive</option>
                      <option value="Georgia, serif">Georgia, serif</option>
                      <option value="Optima">Optima</option>
                    </select>
                  </div>

                  {page.page_type !== 'project' && page.template !== 'static' && page.public &&
                    <div className="form-group">
                      <label htmlFor="font">Filter Type</label>
                      <select
                        value={page.filter_type}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPage((page) => {
                            return { ...page, filter_type: val };
                          });
                        }}
                        className="form-control"
                      >
                        <option value="In Page Close">In Page Close (default)</option>
                        <option value="In Page Open">In Page Open</option>
                        <option value="Pop Up">Pop Up</option>
                        <option value="None">None</option>
                      </select>
                    </div>
                  }

                  {page.use_payments && page.use_stripe && (
                    <div className="form-group">
                      <label htmlFor="stripe_accounts">Stripe Account</label>
                      <select
                        value={page.stripe_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPage((page) => {
                            return { ...page, stripe_id: val };
                          });
                        }}
                        className="form-control"
                      >
                        <option value="">
                          {team.stripe_account_name} (main)
                        </option>
                        {props.stripe_accounts.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {page.page_type == 'project' && page.use_payments && page.public &&
                    <div className="form-group">
                      <label htmlFor="sponsorship_type">Donation Type</label>
                      <select
                        value={page.project_sponsorship_type}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPage({ ...page, project_sponsorship_type: val });
                          setIsDirty(true);
                        }}
                        className="form-control"
                      >
                        <option value="custom">Custom</option>
                        <option value="fixed">Fixed</option>
                        <option value="multiple">Multiple</option>
                      </select>
                    </div>
                  }

                  {page.page_type == 'project' && allowedSponsorshipModal && page.use_payments && page.public &&
                    <div className="form-group">
                      <label htmlFor="customize_sponsorship">Customize Donation</label>
                      <button onClick={() => setShowSponsorshipModal(true)} className="btn btn-primary">Customize</button>
                    </div>
                  }

                  {!page.public && page.is_iframe_page &&
                    <div>
                      <div className="form-group">
                        <label htmlFor="profiles_per_row">iFrame Height (px)</label>
                        <input 
                          type="number" 
                          min={100}
                          step={100} 
                          value={page.iframe_height}
                          onChange={(event) => setPage({...page, iframe_height: event.target.value})}
                          className="form-control"
                        />
                      </div>
                    </div>
                  }
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Community Settings */}
      {!page.public && !page.is_iframe_page && (
        <>
          {/* Community Members Management */}
          <PageMemberships
            pageId={page.id}
            allData={props.memberships_data}
            groups={props.member_groups}
            members={props.members}
          />

          {/* Blocking community members */}
          <PageBlocking
            pageId={page.id}
            allMembers={props.members}
            blockedMembers={blockedMembers}
          />

          {/* Accessibility settings */}
          <div className="card">
            <div className="card-header flex-header">
              <h4 className="card-title">Accessibility Settings</h4>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="donor"
                        checked={page.donor_allowed}
                        onChange={(checked) =>
                          setPage({ ...page, donor_allowed: checked })
                        }
                        disabled={page.private}
                      />
                      <label htmlFor="donor">
                        Allow {titles.donor.toLowerCase()} to join
                      </label>
                    </div>
                    <small className="text-muted">
                      {titles.donor} will not see this{" "}
                      {team.community_title.toLowerCase()} if this option is
                      turned off
                    </small>
                  </div>

                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="user"
                        checked={page.user_allowed}
                        onChange={(checked) =>
                          setPage({ ...page, user_allowed: checked })
                        }
                        disabled={page.private}
                      />
                      <label htmlFor="user">Allow users to join</label>
                    </div>
                    <small className="text-muted">
                      Project managers, sponsorship managers and project
                      co-ordinators will not see this{" "}
                      {team.community_title.toLowerCase()} if this option is
                      turned off
                    </small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="access"
                        checked={page.private}
                        onChange={(checked) => {
                          setPage({
                            ...page,
                            private: checked,
                            beneficiary_allowed: false,
                            donor_allowed: false,
                            user_allowed: false,
                          });
                        }}
                      />
                      <label htmlFor="access">
                        Make this {team.community_title.toLowerCase()} private
                      </label>
                    </div>
                    <small className="text-muted">
                      Private {team.community_title.toLowerCase()} can only be
                      seen by the manually selected members while any user can
                      join when made public based on futher settings
                    </small>
                  </div>

                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="bene"
                        checked={page.beneficiary_allowed}
                        onChange={(checked) =>
                          setPage({ ...page, beneficiary_allowed: checked })
                        }
                        disabled={page.private}
                      />
                      <label htmlFor="bene">
                        Allow {titles.beneficiary.toLowerCase()} to join
                      </label>
                    </div>
                    <small className="text-muted">
                      {titles.beneficiary} will not see this{" "}
                      {team.community_title.toLowerCase} if this option is
                      turned off
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messaging settings */}
          <div className="card">
            <div className="card-header flex-header">
              <h4 className="card-title">Messaging Settings</h4>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="a_o"
                        checked={page.admin_only_messaging}
                        onChange={(checked) => {
                          setPage({
                            ...page,
                            admin_only_messaging: checked,
                            donor_messaging: false,
                            beneficiary_messaging: false,
                            user_messaging: false,
                          });
                        }}
                      />
                      <label htmlFor="a_o">Admin only messaging</label>
                    </div>
                    <small className="text-muted">
                      If this option is turned on, only the manager or team
                      owner will be able to send messages, the{" "}
                      {team.community_title.toLowerCase()} will operate like a
                      channel
                    </small>
                  </div>

                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="d_m"
                        checked={page.donor_messaging}
                        onChange={(checked) =>
                          setPage({ ...page, donor_messaging: checked })
                        }
                        disabled={page.admin_only_messaging}
                      />
                      <label htmlFor="d_m">
                        Allow {team.donor_title.toLowerCase()} messaging
                      </label>
                    </div>
                    <small className="text-muted">
                      {titles.donor} will not be able to send messages to the
                      community unless this option is turned on
                    </small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="u_m"
                        checked={page.user_messaging}
                        onChange={(checked) =>
                          setPage({ ...page, user_messaging: checked })
                        }
                        disabled={page.admin_only_messaging}
                      />
                      <label htmlFor="u_m">Allow user messaging</label>
                    </div>
                    <small className="text-muted">
                      Project managers, sponsorship managers and project
                      co-ordinators will not be able to send messages to the
                      community unless this option is turned on
                    </small>
                  </div>

                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="b_m"
                        checked={page.beneficiary_messaging}
                        onChange={(checked) =>
                          setPage({ ...page, beneficiary_messaging: checked })
                        }
                        disabled={page.admin_only_messaging}
                      />
                      <label htmlFor="b_m">
                        Allow {team.beneficiary_title.toLowerCase()} messaging
                      </label>
                    </div>
                    <small className="text-muted">
                      {titles.beneficiary} will not be able to send messages to
                      the community unless this option is turned on
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* sponsorship card */}
      {(page.public || page.is_iframe_page)&& page.template != "static" && page.page_type !== 'project' && (
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div>
              <h4 className="card-title">Sponsorship Settings</h4>
              <small>
                {showAll && (
                  <>
                    Showing {users.length} of {users.length} records
                  </>
                )}
                {!showAll && (
                  <>
                    Showing {decideUsersCount().length} of {users.length}{" "}
                    records
                  </>
                )}
              </small>
            </div>
            <div className="btn-group">
              <button
                className="btn btn-primary"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll && <>Hide Sponsorships</>}
                {!showAll && <>Show Sponsorships</>}
              </button>
            </div>
          </div>

          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="title">General Sponsorship Amount</label>
                  <input
                    className="form-control"
                    placeholder="100"
                    type="number"
                    name="general"
                    id="general"
                    value={page.general_sponsorship_amount}
                    onChange={(e) =>
                      setPage({
                        ...page,
                        general_sponsorship_amount: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="col-md-6">
                {page.use_stripe && page.use_payments && (
                  <div className="form-group">
                    <div>
                      <ToggleSwitch
                        small
                        id="p_f"
                        checked={page.pass_processing_fee}
                        onChange={(checked) => handleProcessingFee(checked)}
                      />
                      <label htmlFor="p_f">
                        Add payment processing fee to sponsorship amount
                      </label>
                    </div>
                    <small className="text-muted">
                      Please ensure that passing payment processing fees
                      complies with the laws and regulations in your
                      jurisdiction
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label>{team.beneficiary_title} Name</label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label>No of Sponsorships</label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label>Total Amount</label>
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-group">
                  <label>Action</label>
                </div>
              </div>
            </div>

            {users &&
              decideUsersCount() &&
              decideUsersCount().map((user) => (
                <div className="row" key={user.id}>
                  <div className="col-md-4">
                    <div className="form-group">
                      {`${user.first_name} ${user.last_name}`}
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-group">
                      {returnSponsorshipsCount(user)}
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-group">
                      ${returnTotalSponsorshipAmount(user)}
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleOpenModal(user)}
                      >
                        Modify
                      </button>
                    </div>
                  </div>
                </div>
              ))}

            {!users && (
              <div className="col-md-12">
                No users at the moment, add a group to start
              </div>
            )}
          </div>
        </div>
      )}

      {/* custom summary */}
      {page.public && page.template == "sponsorship_video_2" && (
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div>
              <h4 className="card-title">Custom Page Descriptions</h4>
              <small>
                {showAllSummaries && (
                  <>
                    Showing {users.length} of {users.length} records
                  </>
                )}
                {!showAllSummaries && (
                  <>
                    Showing {showUsersSummaries().length} of {users.length}{" "}
                    records
                  </>
                )}
              </small>
            </div>
            <div className="btn-group">
              <button
                className="btn btn-primary"
                onClick={() => setShowAllSummaries(!showAllSummaries)}
              >
                {showAllSummaries && <>Hide Summaries</>}
                {!showAllSummaries && <>Show Summaries</>}
              </button>
            </div>
          </div>

          <div className="card-body">
            <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>{team.beneficiary_title} Name</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Custom Description</label>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Action</label>
                  </div>
                </div>
              </div>

              {users &&
              showUsersSummaries() &&
              showUsersSummaries().map((user) => (
                <div className="row" key={user.id}>
                  <div className="col-md-4">
                    <div className="form-group">
                      {`${user.first_name} ${user.last_name}`}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      {getCustomDescription(user)}
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setDescriptionModal(user)}
                      >
                        Modify
                      </button>
                    </div>
                  </div>
                </div>
              ))}

            {!users && (
              <div className="col-md-12">
                No users at the moment, add a group to start
              </div>
            )}
          </div>
        </div>
      )}

      {/* sponsorship card */}
      {(page.public || page.is_iframe_page) && page.template != "static" && page.collect_sponsor_address && (
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div>
              <h4 className="card-title">Input Field Settings</h4>
              <small>Will be shown and filled in the sponsor signup form</small>
            </div>
          </div>

          <div className="card-body">
            <div className="row align-items-center justify-content-between">
              <div className="col-md-5">
                <div className="form-group">
                    <label htmlFor="title">Input Field Label</label>
                    <input
                      className="form-control"
                      placeholder="Address, Color e.t.c"
                      type="text"
                      name="input-field-label"
                      id="input-field-label"
                      value={inputField.label}
                      onChange={(e) =>
                        setInputField({...inputField, label: e.target.value})
                      }
                    />
                  </div>
              </div>
              <div className="col-md-5">
                <div className="form-group">
                  <label htmlFor="page_link">Custom Field</label>
                  <div className="input-group">
                    <Select
                      className="w-100 select-audience"
                      classNamePrefix="select-audience"
                      value={getCustomField(inputField.id)}
                      onChange={(value) => {
                        if (value.id == 'popup') {
                          setOpenFields(true)
                          return
                        }
                        setInputField({...inputField, id: value.id})
                      }}
                      options={getFieldOptions()}
                      getOptionLabel={(option) => `${option.name}`}
                      getOptionValue={(option) => option.id}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-2" style={{textAlign: 'center', paddingTop: '20px'}}>
                <button className="btn btn-primary" onClick={createField} disabled={!inputField.label || !inputField.id || processingFields} >
                  { editInputField ? 'Update' : 'Create' }
                </button>
              </div>
            </div>

            <div className="row">
              <div className="col-md-5">
                <div className="form-group">
                  <label>Input Field Label</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>Custom Field</label>
                </div>
              </div>
              <div className="col-md-3" style={{textAlign: 'center'}}>
                <div className="form-group">
                  <label>Actions</label>
                </div>
              </div>
            </div>

            {inputFields && inputFields.length > 0 && inputFields.map((field) => (
                <div className="row" key={field.id}>
                  <div className="col-md-5">
                    <div className="form-group">
                      {field.label}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      {getCustomField(field.team_custom_field_id).name}
                    </div>
                  </div>

                  <div className="col-md-3" style={{textAlign: 'center'}}>
                    <div className="form-group">
                      <button
                        className="btn btn-primary btn-sm mr-1"
                        onClick={() => {
                          setEditInputField(field.id)
                          setInputField({label: field.label, id: field.team_custom_field_id})
                        }}
                        disabled={processingFields}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteField(field.id)}
                        disabled={processingFields}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}

            {!inputFields && (
              <div className="col-md-12">
                No input field created yet...
              </div>
            )}
          </div>
        </div>
      )}

      {openFeeModal && (
        <ConfirmationModal
          cancel={() => setOpenFeeModal(false)}
          proceed={() => {
            setPage({ ...page, pass_processing_fee: true });
            setOpenFeeModal(false);
          }}
          message="Please ensure that passing payment processing fees to your donors complies with the laws and regulations in your jurisdiction"
        />
      )}

      {selectedSponsorshipAmount && (
        <PageSponsorshipAmount
          sponsorships={userAmounts}
          user={selectedUser}
          close={(arg) => handleCloseModal(arg)}
          allSponsorships={amounts}
          page={page}
        />
      )} 

      {descriptionModal && 
        <PageSummary 
          user={descriptionModal} 
          page={page} 
          summary={getCustomDescriptionObject(descriptionModal)} 
          close={() => {setDescriptionModal(null)}}
          proceed={(value) => setCustomSummaries(value)}
         />
      }

      {openFields &&
        <CustomField 
          close={() => setOpenFields(false)} 
          proceed={(value) => setCustomFields(value)} 
          reservedNames={props.reservedNames} 
          customFields={customFields}
        />
      }

      {showSponsorshipModal &&
        <CustomizeSponsorshipModal 
          type={page.project_sponsorship_type}
          amount={page.project_fixed_amount}
          plans={page.project_sponsorship_plans}
          close={() => setShowSponsorshipModal(false)} 
          proceed={(amount, plans) => {
            setPage({ ...page, project_fixed_amount: amount, project_sponsorship_plans: plans })
            setShowSponsorshipModal(false)
          }}
        />
      }
    </>
  );
}

PagesEdit.propTypes = {
  page: PropTypes.object,
};
